import type { Ref, WatchStopHandle } from 'vue'
import { ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import {
  Viewer,
  Math as CesiumMath,
  Cartesian3,
  ConstantProperty,
  createGooglePhotorealistic3DTileset,
  DataSource,
  DeveloperError,
  Ellipsoid,
  EllipsoidTerrainProvider,
  Entity,
  EntityCollection,
  Google2DImageryProvider,
  MapMode2D,
  RequestErrorEvent,
  RuntimeError,
  ScreenSpaceEventType,
  Terrain,
  TileProviderError,
  WebMercatorProjection,
} from 'cesium'
import { setGoogle2DImagery, setMapboxImagery, clearLayerCache } from './shared'
import { cesiumConfig } from '@/config/cesium.config'
import type { ICesiumViewer } from '@/types/cesium.interface'
import { FPVService } from '@/handlers/fpv.service'
import log from '@/utils/useLogger'
import useDroneLocation from '@/composables/Drone/useDroneLocation'
import useUserSettings from '@/composables/useUserSettings'
import { viewsStore } from '@/store/views'

const mapLabelsVisible = ref(true)

export const getMapLabelsVisible = (): boolean => mapLabelsVisible.value

export const setMapLabelsVisible = (show: boolean): void => {
  mapLabelsVisible.value = show
}

/**
 * Walk every entity in the viewer (root collection + every datasource) and
 * toggle `entity.label.show` for entities that actually have a label.
 * Uses a fresh ConstantProperty so the value is not clobbered by subsequent
 * property assignments from entity creators.
 */
function applyLabelVisibilityToViewer(viewer: Viewer | null, show: boolean): void {
  if (!viewer || viewer.isDestroyed()) return

  const visibility = new ConstantProperty(show)

  const applyToCollection = (entities: EntityCollection | undefined) => {
    if (!entities) return
    const values = entities.values
    for (let i = 0; i < values.length; i++) {
      const entity = values[i]
      if (entity && entity.label) {
        entity.label.show = visibility
      }
    }
  }

  applyToCollection(viewer.entities)

  const dataSources = viewer.dataSources
  for (let i = 0; i < dataSources.length; i++) {
    applyToCollection(dataSources.get(i).entities)
  }

  // Hide HTML overlay labels (zones, annotations, POI, etc.) created via
  // `useEntityLabels()`. They live in `#entity-labels` containers appended
  // to `viewer.container` and are NOT Cesium LabelGraphics, so the entity
  // pass above doesn't touch them. Setting `display:none` on the container
  // hides every label inside regardless of the per-frame `style.display`
  // writes performed by the label update loop.
  applyDomLabelVisibility(viewer, show)
}

function applyDomLabelVisibility(viewer: Viewer | null, show: boolean): void {
  if (!viewer || viewer.isDestroyed()) return
  const root = viewer.container as HTMLElement | undefined
  if (!root) return
  const containers = root.querySelectorAll<HTMLElement>('#entity-labels')
  containers.forEach((el) => {
    el.style.display = show ? '' : 'none'
  })
}

// Create a singleton instance
class CesiumServiceClass implements ICesiumViewer {
  public viewer = ref<Viewer | null>(null)
  public isInitialized = ref(false)
  public isTilesetLoading = ref(false)
  public worldTileSet = ref<any>(null)
  public worldTileSetLabelsLayer = ref<any>(null)
  public droneEntity = ref<Entity | null>(null)
  public isErrorHandlerSet = ref(false)
  public errorCount = ref(0)
  public lastErrorTime = ref(0)
  public readonly ERROR_THRESHOLD = cesiumConfig.errorHandling.threshold
  public readonly ERROR_RESET_TIME = cesiumConfig.errorHandling.resetTime

  // Tracks unsubscribe callbacks for label-visibility wiring so they can
  // be torn down when the viewer is destroyed.
  private labelVisibilityDisposers: Array<() => void> = []
  private dataSourceListenerDisposers = new Map<DataSource, () => void>()
  private labelOverlayObserver: MutationObserver | null = null

  public async init(map: Ref<HTMLElement | null>): Promise<void> {
    try {
      if (!map.value) return
      const { droneLocation } = useDroneLocation()
      const { settings } = useUserSettings()

      // Configure Cesium to use a custom worker configuration
      // This helps prevent DataCloneError when passing data to workers
      // Cesium.RequestScheduler.requestsByServer = {}
      // Cesium.RequestScheduler.maximumRequests = 50

      // Set up a custom worker configuration
      if (typeof window !== 'undefined') {
        ;(window as any).CESIUM_BASE_URL = '/cesium'
      }

      const { base, overlay } = await setGoogle2DImagery()
      this.viewer.value = new Viewer(map.value, {
        terrain: Terrain.fromWorldTerrain({
          requestVertexNormals: true,
          requestWaterMask: true,
        }),
        baseLayer: false,
        baseLayerPicker: false,
        // animation: false,
        homeButton: false,
        sceneModePicker: false,
        navigationHelpButton: false,
        // timeline: false,
        fullscreenButton: false,
        infoBox: false,
        selectionIndicator: false,
        geocoder: true,
        sceneMode: cesiumConfig.defaultSceneMode,
        // INFINITE_SCROLL (the default) makes camera rotation impossible in 2D
        mapMode2D: MapMode2D.ROTATE,
        mapProjection: new WebMercatorProjection(Ellipsoid.WGS84),
      })
      // Disable default double-click zoom behavior
      this.viewer.value.cesiumWidget.screenSpaceEventHandler.removeInputAction(
        ScreenSpaceEventType.LEFT_DOUBLE_CLICK,
      )
      if (settings.value.mapStyle === 'google-maps') {
        this.viewer.value.imageryLayers.add(base)
        this.viewer.value.imageryLayers.add(overlay)
      } else {
        const mapBoxStyle = await setMapboxImagery(settings.value.mapStyle)
        this.viewer.value.imageryLayers.addImageryProvider(mapBoxStyle)
      }
      if (droneLocation.value) {
        this.viewer.value.camera.setView({
          destination: Cartesian3.fromDegrees(
            droneLocation.value.longitude,
            droneLocation.value.latitude,
            1000,
          ),
        })
      }
      // this.viewer.value.scene.globe.depthTestAgainstTerrain = true
      this.viewer.value.cesiumWidget.creditContainer.className = 'display-none'
      this.viewer.value.clock.shouldAnimate = true
      this.viewer.value.clock.multiplier = 1.0

      this.viewer.value.scene.globe.tileCacheSize = 2000
      // this.viewer.value.scene.globe.maximumScreenSpaceError = 2
      this.viewer.value.scene.globe.enableLighting = false
      this.viewer.value.scene.postProcessStages.fxaa.enabled = true
      this.viewer.value.resolutionScale = window.devicePixelRatio

      if (settings.value.tiles3dMode && settings.value.tiles3dMode !== 'none') {
        await this.add3dGooglePhotorealistic()
        // Add labels if mode is 'photorealistic-with-labels'
        if (settings.value.tiles3dMode === 'photorealistic-with-labels') {
          await this.toggle3dGooglePhotorealisticLabels(true)
        }
      }
      // Set up error handling
      this.setupErrorHandling()

      // Wire up dynamic label visibility tied to flyViewState (CAM_VIEW = hide).
      this.setupMapLabelVisibility()

      // Set initialization flag and call callback if provided
      this.isInitialized.value = true
    } catch (error) {
      log.error('Error initializing Cesium:', error)
      throw error // Re-throw the error to allow proper error handling by the caller
    }
  }

  /**
   * Bind `mapLabelsVisible` to `flyViewState` and apply visibility to the
   * viewer's entities + every datasource (including entities/datasources
   * added after initialization). Owns its own teardown via
   * `labelVisibilityDisposers`.
   */
  private setupMapLabelVisibility(): void {
    const viewer = this.viewer.value
    if (!viewer) return

    const { flyViewState } = storeToRefs(viewsStore())

    // Sync flag from current view state and on every change.
    mapLabelsVisible.value = flyViewState.value !== 'CAM_VIEW'
    const stopViewWatch: WatchStopHandle = watch(
      flyViewState,
      (state) => {
        mapLabelsVisible.value = state !== 'CAM_VIEW'
      },
      { immediate: false },
    )

    // Apply visibility whenever the flag flips. Kept separate from the view
    // watch so `setMapLabelsVisible` can later drive a user toggle.
    const stopFlagWatch: WatchStopHandle = watch(
      mapLabelsVisible,
      (show) => {
        applyLabelVisibilityToViewer(this.viewer.value, show)
      },
      { immediate: true },
    )

    // Composables (drones, zones, docks, waypoints, POI, missions, ...) add
    // entities reactively after the view changes, so we have to re-apply
    // visibility whenever an entity collection grows.
    const wireEntityCollection = (entities: EntityCollection | undefined) => {
      if (!entities) return () => {}
      const remove = entities.collectionChanged.addEventListener(
        (_collection: EntityCollection, added: Entity[]) => {
          if (added && added.length > 0) {
            applyLabelVisibilityToViewer(this.viewer.value, mapLabelsVisible.value)
          }
        },
      )
      return typeof remove === 'function' ? remove : () => {}
    }

    const rootRemove = wireEntityCollection(viewer.entities)

    // Wire all current datasources, and keep wiring new ones.
    const wireDataSource = (ds: DataSource) => {
      if (!ds || this.dataSourceListenerDisposers.has(ds)) return
      const dispose = wireEntityCollection(ds.entities)
      this.dataSourceListenerDisposers.set(ds, dispose)
    }

    for (let i = 0; i < viewer.dataSources.length; i++) {
      wireDataSource(viewer.dataSources.get(i))
    }

    const removeDataSourceAdded = viewer.dataSources.dataSourceAdded.addEventListener(
      (_collection, dataSource: DataSource) => {
        wireDataSource(dataSource)
        // Newly-added datasources may already carry labeled entities.
        applyLabelVisibilityToViewer(this.viewer.value, mapLabelsVisible.value)
      },
    )

    const removeDataSourceRemoved = viewer.dataSources.dataSourceRemoved.addEventListener(
      (_collection, dataSource: DataSource) => {
        const dispose = this.dataSourceListenerDisposers.get(dataSource)
        if (dispose) {
          dispose()
          this.dataSourceListenerDisposers.delete(dataSource)
        }
      },
    )

    // HTML overlay labels (zones, annotations, POI, ...) live in DOM
    // containers (`#entity-labels`) that are appended to `viewer.container`
    // lazily, often AFTER the user has already switched to CAM_VIEW. Watch
    // the container subtree so newly-added overlays inherit the current
    // visibility immediately.
    const containerEl = viewer.container as HTMLElement | undefined
    if (containerEl && typeof MutationObserver !== 'undefined') {
      this.labelOverlayObserver = new MutationObserver((mutations) => {
        let needsApply = false
        for (const m of mutations) {
          if (m.addedNodes && m.addedNodes.length > 0) {
            for (const node of Array.from(m.addedNodes)) {
              if (
                node instanceof HTMLElement &&
                (node.id === 'entity-labels' ||
                  node.querySelector?.('#entity-labels'))
              ) {
                needsApply = true
                break
              }
            }
          }
          if (needsApply) break
        }
        if (needsApply) {
          applyDomLabelVisibility(this.viewer.value, mapLabelsVisible.value)
        }
      })
      this.labelOverlayObserver.observe(containerEl, { childList: true, subtree: true })
    }

    this.labelVisibilityDisposers.push(
      stopViewWatch,
      stopFlagWatch,
      rootRemove,
      typeof removeDataSourceAdded === 'function' ? removeDataSourceAdded : () => {},
      typeof removeDataSourceRemoved === 'function' ? removeDataSourceRemoved : () => {},
      () => {
        if (this.labelOverlayObserver) {
          this.labelOverlayObserver.disconnect()
          this.labelOverlayObserver = null
        }
      },
    )
  }

  private teardownMapLabelVisibility(): void {
    for (const dispose of this.labelVisibilityDisposers) {
      try {
        dispose()
      } catch (e) {
        log.warn('Error disposing label visibility listener:', e)
      }
    }
    this.labelVisibilityDisposers = []

    for (const dispose of this.dataSourceListenerDisposers.values()) {
      try {
        dispose()
      } catch (e) {
        log.warn('Error disposing datasource label listener:', e)
      }
    }
    this.dataSourceListenerDisposers.clear()
  }

  // Set up error handling to prevent runtime error modals from showing up and crashing the app
  private setupErrorHandling() {
    try {
      if (this.isErrorHandlerSet.value) return

      // Prevent default Cesium error panel from showing
      if (this.viewer.value) {
        this.viewer.value.cesiumWidget.showErrorPanel = () => {
          // Do nothing to prevent the error panel from showing
        }
      }

      // Add CSS to hide any error panels that might be created
      const style = document.createElement('style')
      style.textContent = `
        .cesium-widget-errorPanel {
          display: none !important;
        }
      `
      document.head.appendChild(style)

      // Global error handling for uncaught Cesium errors
      window.addEventListener('error', (event) => {
        const error = event.error
        if (error && error.toString) {
          const errorString = error.toString()

          // Check if it's a Cesium error
          if (
            errorString.includes('Cesium') ||
            error instanceof RuntimeError ||
            error instanceof DeveloperError
          ) {
            if (error instanceof DeveloperError) {
              // DeveloperError: indicates a bug in the calling code
              log.error('CESIUM DEVELOPER ERROR (Bug in code):', error.message, error.stack)

              // We don't show these to users as they indicate code bugs
              // But we can break in development to help debugging
              if (process.env.NODE_ENV === 'development') {
                debugger // This will pause execution in dev tools
              }

              // Prevent default handling to avoid crashing the app
              event.preventDefault()
              return false
            } else if (error instanceof RuntimeError) {
              // RuntimeError: indicates an exception that may occur at runtime
              log.error('CESIUM RUNTIME ERROR:', error.message)
              this.showError(`Cesium Error: ${error.message || 'Unknown error'}`)
              // Prevent default handling to avoid crashing the app
              event.preventDefault()
              return false
            } else if (error instanceof RequestErrorEvent) {
              // RequestErrorEvent: indicates a network or server issue
              // Access the statusCode and response properties of RequestErrorEvent
              const statusCode = (error as any).statusCode || 'unknown'
              const responseText = (error as any).response || 'Connection problem'
              log.warn(`CESIUM REQUEST ERROR (${statusCode}):`, responseText)
              this.showError(`Network Error (${statusCode}): ${responseText}`)

              event.preventDefault()
              return false
            } else if (error.name === 'TileProviderError') {
              // TileProviderError: problem loading tiles
              log.warn('CESIUM TILE PROVIDER ERROR:', error.message || errorString)
              // Don't show to users unless critical
              if ((error as any).timesRetried > 3) {
                this.showError(`Map Tile Error: ${error.message || 'Failed to load map tiles'}`)
              }

              event.preventDefault()
              return false
            }
          }

          // Handle general TypeError including "undefined" property access errors
          if (
            error instanceof TypeError &&
            (errorString.includes('undefined') || errorString.includes('null'))
          ) {
            log.error('CESIUM PROPERTY ACCESS ERROR:', errorString)
            this.showError(`Rendering error: ${error.message || 'Property access error'}`)
            // Try to recover with a render
            if (this.viewer.value) {
              try {
                this.viewer.value.scene.requestRender()
              } catch (e) {
                log.error('Failed to recover from property access error:', e)
              }
            }
            // Prevent the default error dialog
            event.preventDefault()
            return false
          }
        }
      })

      // Set up the renderError event listener - this is a key Cesium error event
      this.viewer.value?.scene.renderError.addEventListener((scene, error) => {
        const now = Date.now()
        // Set useDefaultRenderLoop to true to recover from rendering stop
        if (this.viewer.value) {
          this.viewer.value.useDefaultRenderLoop = true
          // window.location.reload()
        }

        // Check if this is a DataCloneError
        if (error && error.message && error.message.includes('DataCloneError')) {
          log.warn(
            'Cesium render error: DataCloneError detected. This is likely due to passing non-serializable objects to workers.',
          )
          // Increment error count but don't show error to user
          this.errorCount.value++
          this.lastErrorTime.value = now
          return
        }

        // Handle other render errors
        this.errorCount.value++
        this.lastErrorTime.value = now

        // Get detailed error information
        let errorDetails = 'Unknown error'
        if (error instanceof RuntimeError) {
          errorDetails = `RuntimeError: ${error.message}`
          log.error('Cesium render error stack:', error.stack)
        } else if (error instanceof Error) {
          errorDetails = `${error.name}: ${error.message}`
        }

        log.error('Cesium render error:', errorDetails)
        this.showError(`Rendering error: ${error.message || 'Unknown error'}`)

        // Special handling for the 'rectangles' property error
        if (error instanceof TypeError && error.message.includes('rectangles')) {
          log.warn('Detected rectangles property error, attempting to recover')

          // Try to recover by resetting the scene's primitives
          try {
            if (this.viewer.value && !this.viewer.value.isDestroyed()) {
              // Clear all primitives except the globe
              const primitives = this.viewer.value.scene.primitives
              const count = primitives.length

              // Remove all primitives except the globe
              for (let i = count - 1; i >= 0; i--) {
                const primitive = primitives.get(i)
                if (primitive && primitive !== this.viewer.value.scene.globe) {
                  primitives.remove(primitive)
                }
              }

              // Reset the worldTileSet reference
              this.worldTileSet.value = null

              // Force a render to update the view
              this.viewer.value.scene.requestRender()
            }
          } catch (recoveryError) {
            log.error('Failed to recover from rectangles property error:', recoveryError)
          }
        }

        // Try to recover from rendering errors by resetting the scene
        try {
          // Force a render to attempt recovery
          this.viewer.value?.scene.requestRender()
        } catch (e) {
          log.error('Failed to recover from rendering error:', e)
        }

        // If we're getting too many errors, try to recover
        if (this.errorCount.value >= this.ERROR_THRESHOLD) {
          log.warn('Too many rendering errors, applying performance optimizations')

          // 1. Remove 3D buildings if they exist
          if (this.worldTileSet.value) {
            try {
              this.viewer.value?.scene.primitives.remove(this.worldTileSet.value)
            } catch (e) {
              log.warn('Error removing 3D buildings:', e)
            }

            this.viewer.value!.scene.globe.show = true
            this.worldTileSet.value = null
          }

          // 2. Set requestRenderMode to true to reduce rendering load
          this.viewer.value!.scene.requestRenderMode = true
          this.viewer.value!.scene.maximumRenderTimeChange = 0.0 // Only render on demand

          // 3. Reset error count to avoid multiple recoveries
          this.errorCount.value = 0
        }
      })

      // Listen for tile load errors on terrain
      const terrainProviderErrorEvent = this.viewer.value?.terrainProvider?.errorEvent
      if (terrainProviderErrorEvent) {
        terrainProviderErrorEvent.addEventListener((error) => {
          log.warn('Terrain provider error:', error)

          // If terrain loading fails, switch to ellipsoid terrain
          if (this.viewer.value && !this.viewer.value.isDestroyed()) {
            log.warn('Terrain error detected, switching to ellipsoid terrain')

            try {
              // Store the current camera position
              const camera = this.viewer.value.scene.camera
              const position = camera.position.clone()

              // Switch to ellipsoid terrain
              this.viewer.value.scene.terrainProvider = new EllipsoidTerrainProvider()

              // Reset the camera to the same position
              camera.position = position

              // Force a render to update the view
              this.viewer.value.scene.requestRender()
            } catch (switchError) {
              log.error('Error switching terrain provider:', switchError)
            }
          }
        })
      }

      // Add TileProviderError handling for imagery layers
      this.viewer.value?.imageryLayers.layerAdded.addEventListener((layer) => {
        if (layer && layer.imageryProvider && (layer.imageryProvider as any).errorEvent) {
          ;(layer.imageryProvider as any).errorEvent.addEventListener((err: TileProviderError) => {
            // Log but don't show to user unless it becomes persistent
            log.warn('Imagery provider error:', err.message)
            if ((err as any).timesRetried > 5) {
              this.showError(`Map imagery error: ${err.message}`)
            }

            // Return true to retry loading the tile
            return true
          })
        }
      })

      // Use Cesium.formatError to prettify Cesium errors
      // const originalConsoleError = log.error
      // log.error = function (...args) {
      //   if (
      //     args.length > 0 &&
      //     args[0] instanceof Error &&
      //     args[0].message &&
      //     args[0].message.includes('Cesium')
      //   ) {
      //     // Format Cesium errors for better readability
      //     args[0] = Cesium.formatError(args[0])
      //   }
      //   originalConsoleError.apply(console, args)
      // }

      // Detect WebGL context lost events
      const canvas = this.viewer.value?.canvas
      canvas?.addEventListener('webglcontextlost', (event) => {
        event.preventDefault()
        this.showError('WebGL context lost. The application will attempt to recover.')

        // Attempt to restore after a delay
        setTimeout(() => {
          try {
            if (this.viewer.value) {
              // Force a re-render
              this.viewer.value.scene.requestRender()
            }
          } catch (e) {
            log.error('Failed to recover from WebGL context loss:', e)
            this.showError('Failed to recover from WebGL context loss. Please refresh the page.')
          }
        }, 2000)
      })

      // Mark error handler as set to prevent duplicate handlers
      this.isErrorHandlerSet.value = true
    } catch (error) {
      log.error('Error setting up Cesium error handlers:', error)
    }
  }

  // Helper method to display errors to the user
  public showError(
    message: string,
    duration: number = cesiumConfig.errorHandling.errorDisplayDuration,
  ) {
    // Create a visible error message for users
    const errorDiv = document.createElement('div')
    errorDiv.style.position = 'fixed'
    errorDiv.style.bottom = '20px'
    errorDiv.style.right = '20px'
    errorDiv.style.backgroundColor = 'rgba(255, 0, 0, 0.7)'
    errorDiv.style.color = 'white'
    errorDiv.style.padding = '10px'
    errorDiv.style.borderRadius = '5px'
    errorDiv.style.zIndex = '9999'
    errorDiv.textContent = message

    document.body.appendChild(errorDiv)

    // Auto-remove after specified duration
    setTimeout(() => {
      if (document.body.contains(errorDiv)) {
        document.body.removeChild(errorDiv)
      }
    }, duration)
  }

  // Set the terrain provider based on the selected type
  public async setTerrainProvider(): Promise<void> {
    if (!this.viewer.value) return
    try {
      const terrainProvider = new EllipsoidTerrainProvider()
      this.viewer.value.scene.terrainProvider = terrainProvider
    } catch (error) {
      log.error('Failed to set terrain provider:', error)
    }
  }

  // Set camera to view all waypoints in a route
  public async flyToRoute(waypoints: any[]): Promise<void> {
    // todo: change the fy implementation to just go to the first waypoint
    try {
      if (!this.viewer.value || !waypoints || waypoints.length === 0) return
      const position = Cartesian3.fromDegrees(
        Number(waypoints[0].longitude),
        Number(waypoints[0].latitude),
        Number(waypoints[0].altitude) + Number(waypoints[0].height) + 800,
      )
      this.viewer.value.camera.flyTo({
        destination: position,
        duration: 1.0,
      })
      return
    } catch (error) {
      log.error('Error flying to route:', error)
    }
  }

  // Cleanup method to properly dispose resources
  public cleanup(): void {
    try {
      // Clean up FPV service first
      FPVService.cleanup()

      // Drop label-visibility watchers and entity/datasource subscriptions
      // before the viewer is destroyed so we don't leak Cesium event handles.
      this.teardownMapLabelVisibility()

      if (this.viewer.value && !this.viewer.value.isDestroyed()) {
        // Clear layer cache before destroying viewer
        clearLayerCache(this.viewer.value)
        this.viewer.value.destroy()
        this.viewer.value = null
      }

      // Reset all static properties
      this.viewer.value = null
      this.isInitialized.value = false
      this.isErrorHandlerSet.value = false
      this.errorCount.value = 0
      this.isTilesetLoading.value = false
      this.worldTileSet.value = null
    } catch (error) {
      log.error('Error during Cesium cleanup:', error)
      throw error // Re-throw the error to allow proper error handling by the caller
    }
  }

  public async add3dGooglePhotorealistic(): Promise<boolean> {
    try {
      if (!this.viewer.value || this.isTilesetLoading.value) {
        log.warn('Cannot add 3D tileset: viewer not initialized or tileset already loading')
        this.worldTileSet.value = null
        // Ensure we have the correct terrain provider after error
        await this.setTerrainProvider()
        return false
      }

      this.isTilesetLoading.value = true

      if (!this.worldTileSet.value) {
        try {
          // Add defensive check to ensure viewer is still valid
          if (!this.viewer.value || this.viewer.value.isDestroyed()) {
            log.warn('Viewer is no longer valid, aborting tileset creation')
            this.worldTileSet.value = null
            // Ensure we have the correct terrain provider after error
            await this.setTerrainProvider()
            return false
          }

          this.worldTileSet.value = await createGooglePhotorealistic3DTileset({
            onlyUsingWithGoogleGeocoder: true,
          })
          
          // Add defensive check again before adding to scene
          if (!this.viewer.value || this.viewer.value.isDestroyed()) {
            log.warn('Viewer is no longer valid, aborting tileset addition')
            this.worldTileSet.value = null
            // Ensure we have the correct terrain provider after error
            await this.setTerrainProvider()
            return false
          }

          try {
            this.viewer.value.scene.primitives.add(this.worldTileSet.value)
          } catch (addError) {
            log.error('Error adding tileset to scene:', addError)
            this.worldTileSet.value = null
            // Ensure we have the correct terrain provider after error
            await this.setTerrainProvider()
            return false
          }
        } catch (tilesetError) {
          log.error('Error creating Google Photorealistic 3D tileset:', tilesetError)
          this.worldTileSet.value = null
          // Ensure we have the correct terrain provider after error
          await this.setTerrainProvider()
          return false
        }
      }

      if (this.worldTileSet.value) {
        // Add defensive check before showing tileset
        if (!this.viewer.value || this.viewer.value.isDestroyed()) {
          log.warn('Viewer is no longer valid, aborting tileset display')
          this.worldTileSet.value = null
          // Ensure we have the correct terrain provider after error
          await this.setTerrainProvider()
          return false
        }

        try {
          this.worldTileSet.value.show = true
          log.info('3D Google Photorealistic tileset is now visible')
          return true
        } catch (showError) {
          log.error('Error showing tileset:', showError)
          this.worldTileSet.value = null
          // Ensure we have the correct terrain provider after error
          await this.setTerrainProvider()
          return false
        }
      }

      return false
    } catch (err) {
      log.error('Error adding 3d google photorealistic:', err)
      this.worldTileSet.value = null
      // Ensure we have the correct terrain provider after error
      await this.setTerrainProvider()
      return false
    } finally {
      this.isTilesetLoading.value = false
    }
  }

  public async toggle3dGooglePhotorealistic(show: boolean): Promise<boolean> {
    if (!this.viewer.value) {
      log.warn('Cannot toggle 3D tileset: viewer not initialized')
      return false
    }

    try {
      // If we want to show the 3D buildings
      if (show) {
        // If the tileset is already loaded, just show it
        if (this.worldTileSet.value) {
          try {
            this.worldTileSet.value.show = true
            log.info('Showing existing 3D Google Photorealistic tileset')
            return true
          } catch (error) {
            log.error('Error showing existing tileset:', error)
            // Reset the tileset if there's an error showing it
            this.worldTileSet.value = null
            // Ensure we have the correct terrain provider after error
            await this.setTerrainProvider()
            return false
          }
        }

        // Otherwise, load it first
        log.info('Loading 3D Google Photorealistic tileset')
        const success = await this.add3dGooglePhotorealistic()
        if (!success) {
          log.warn('Failed to load 3D Google Photorealistic tileset')
          // Ensure worldTileSet is null if loading failed
          this.worldTileSet.value = null
          // Ensure we have the correct terrain provider after error
          await this.setTerrainProvider()
          return false
        }
        return true
      }
      // If we want to hide the 3D buildings
      else if (this.worldTileSet.value) {
        // Only hide if it's already loaded
        try {
          this.worldTileSet.value.show = false
          log.info('Hiding 3D Google Photorealistic tileset')
          return true
        } catch (error) {
          log.error('Error hiding tileset:', error)
          // Reset the tileset if there's an error hiding it
          this.worldTileSet.value = null
          // Ensure we have the correct terrain provider after error
          await this.setTerrainProvider()
          return false
        }
      }
      // If we want to hide and it's not loaded yet, do nothing
      return true
    } catch (err) {
      log.error('Error toggling 3d google photorealistic:', err)
      // Reset the tileset on any error during toggling
      this.worldTileSet.value = null
      // Ensure we have the correct terrain provider after error
      await this.setTerrainProvider()
      return false
    }
  }

  /**
   * Toggle Google Maps labels overlay on the 3D Photorealistic tileset
   * @param show - Whether to show or hide labels
   * @returns Promise<boolean> - Success status
   */
  public async toggle3dGooglePhotorealisticLabels(show: boolean): Promise<boolean> {
    if (!this.viewer.value) {
      log.warn('Cannot toggle 3D tileset labels: viewer not initialized')
      return false
    }

    if (!this.worldTileSet.value) {
      log.warn('Cannot toggle labels: 3D tileset not loaded')
      return false
    }

    try {
      if (show) {
        // Add labels if not already added
        if (!this.worldTileSetLabelsLayer.value) {
          const googleLabelsProvider = await Google2DImageryProvider.fromIonAssetId({
            assetId: '3830184',
            overlayLayerType: 'layerRoadmap',
          })
          this.worldTileSetLabelsLayer.value = this.worldTileSet.value.imageryLayers.addImageryProvider(googleLabelsProvider)
          log.info('Added Google Maps labels to 3D Photorealistic tileset')
        } else {
          // Show existing labels layer
          this.worldTileSetLabelsLayer.value.show = true
          log.info('Showing existing labels on 3D Photorealistic tileset')
        }
        return true
      } else {
        // Hide labels
        if (this.worldTileSetLabelsLayer.value) {
          this.worldTileSetLabelsLayer.value.show = false
          log.info('Hiding labels on 3D Photorealistic tileset')
        }
        return true
      }
    } catch (err) {
      log.error('Error toggling 3D tileset labels:', err)
      return false
    }
  }
}

// Export a singleton instance
export const CesiumService = new CesiumServiceClass()
